import React, { useEffect, useState } from 'react';
import { ArrowBackIos } from '@mui/icons-material';
import AttachmentIcon from '@mui/icons-material/Attachment';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import PersonIcon from '@mui/icons-material/Person';
import MapOutlinedIcon from '@mui/icons-material/MapOutlined';
import { Box, Button, Container, Grid, LinearProgress, Paper, TextareaAutosize } from '@mui/material';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import moment from 'moment-timezone';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useRecoilState, useRecoilValue } from 'recoil';

import customer from '../../assets/customer.jpg';
import logo from '../../assets/logo.svg';
import { selectedAppealState } from '../../atoms/DriverAppealState';
import { DRIVER_APPEALS_DATETIME_FORMAT } from '../../utils/dateUtil';
import { appealFiles, getDriverAppealsComments, getDriverAppealsDetail, replyAppeal, updateAppealStatus } from '../../repositories/HttpRepository';
import useStyles from './styles';
import { userState } from '../../atoms/States';

Detail.propTypes = {};

const STATUS_CREATED = 'CREATED';
const STATUS_IN_PROGRESS = 'IN_PROGRESS';
const STATUS_APPROVED = 'APPROVED';
const STATUS_REJECTED = 'REJECTED';

function Detail(props) {
  const navigate = useNavigate();
  const { classes } = useStyles();
  const { id } = useParams();
  const [loadingComments, setLoadingComments] = useState(false);
  const [answer, setAnswer] = useState(null);
  const [replies, setReplies] = useState([]);
  const [open, setOpen] = useState(false);
  const [alertTitle, setAlertTitle] = useState('');
  const [alertContent, setAlertContent] = useState('');
  const [selected, setSelected] = useRecoilState(selectedAppealState);
  const currentUser = useRecoilValue(userState);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [error, setError] = useState(null);
  const [files, setFiles] = useState(null);
  const [driverLogo, setDriverLogo] = useState(customer);

  if (!selected.id && !id) {
    navigate('/driver-appeals');
  }

  useEffect(() => {
    if (!selected.id && !!id) {
      getDriverAppealsDetail(id).then((resp) => {
        if (resp.ok) {
          setSelected(resp.data);
        } else {
          setSelected({});
        }
      });
    }

    setLoadingComments(true);
    getDriverAppealsComments(id).then((resp) => {
      if (!resp.ok) {
        return false;
      }
      if (resp.data.items) {
        setReplies(resp.data.items);
      }
      setLoadingComments(false);
    });

    appealFiles(id).then((resp) => {
      if (!resp.ok) {
        return false;
      }
      if (resp.data) {
        setFiles(resp.data);
      }
    });
  }, []);

  const handleChange = (e) => {
    setAnswer(e.target.value);
    setError('');
  };

  const sendMessage = (status) => {
    if (!answer) {
      setError('Message cannot be empty');
      return false;
    }
    setSelectedStatus(status);
    if (status === STATUS_APPROVED) {
      setAlertTitle('Do you want to approve this appeal?');
      setAlertContent('This appeal will be approved and a message will be sent to the driver?');
    } else if (status === STATUS_REJECTED) {
      setAlertTitle('Do you want to reject this appeal?');
      setAlertContent('This appeal will be rejected and a message will be sent to the driver?');
    }
    setOpen(true);
  };

  function handleBack(e) {
    navigate('/driver-appeals');
  }

  const replyDisabled = !!(selected.status && [STATUS_REJECTED, STATUS_APPROVED].includes(selected.status) && replies);
  const classStatus = selected.status ? 'status ' + selected.status.toLowerCase() : '';

  const handleClose = () => {
    setOpen(false);
    setAnswer('');
  };

  const handleAccept = (status) => {
    if (!answer) {
      setError('Message cannot be empty');
      return false;
    }

    setOpen(false);

    // update status
    updateAppealStatus(id, status, { remark: answer });

    const params = {
      appeal_id: id,
      content: answer,
    };

    replyAppeal(params).then((resp) => {
      if (!resp.ok) {
        return false;
      }
      let tmp = [...replies];
      tmp.push({
        is_admin: true,
        name: 'Jitsu',
        created_at: moment.tz(moment.tz.guess()).format(DRIVER_APPEALS_DATETIME_FORMAT),
        content: answer,
        user_name: currentUser?.user?.name,
      });
      setReplies(tmp);

      // update selected
      setSelected({ ...selected, status: status });
      setAnswer('');
      toast.success('Update successfully !');
    });
  };

  const onloadImage = () => {
    if (selected.driver_logo) {
      setDriverLogo(selected.driver_logo);
    }
  };

  return (
    <Container maxWidth={false} className={classes.root}>
      <div className={classes.root}>
        <Box sx={{ mb: 2 }}>
          <Button variant="contained" startIcon={<ArrowBackIos />} elevation={0} onClick={handleBack} p={20} color="inherit">
            {' '}
            Back
          </Button>
        </Box>
        <Grid container spacing={0} className={classes.main}>
          <Grid size={2}>
            <Paper className={classes.driverInfos}>
              <img src={driverLogo} onLoad={onloadImage} alt="Driver" />
              <Box className={classes.driverName}>{selected.driver_name}</Box>
              <Box>{selected.driver_email}</Box>
              <Box>
                {!!selected.driver_id && (
                  <span>
                    ID: <strong>{selected.driver_id}</strong>
                  </span>
                )}
              </Box>
            </Paper>
          </Grid>

          <Grid size={6}>
            <Paper className={classes.reasonSection}>
              <h3 className={classes.title}>
                Appeal Details
                <Button variant="contained" className={classStatus}>
                  {selected.status}
                </Button>
              </h3>
              <Box className={classes.row}>
                <label>Full Name:</label>
                <span>{selected.name}</span>
              </Box>
              <Box className={classes.row}>
                <Box component="span">
                  <PhoneIcon /> {selected.phone_number}
                </Box>
                <Box component="span" sx={{ ml: 1 }}>
                  <EmailIcon /> {selected.email}
                </Box>
                <Box component="span" sx={{ ml: 1 }}>
                  <PersonIcon /> {selected.driver_id}
                </Box>
                <Box component="span" sx={{ ml: 1 }}>
                  <MapOutlinedIcon /> {selected.region_code || '-'}
                </Box>
              </Box>
              <Box className={classes.row}>
                <label>Attach files: </label>
                {files?.map((file, index) => (
                  <a target="_blank" className="file" href={file.path} key={`file-${file.id || index}`}>
                    <AttachmentIcon>abc</AttachmentIcon>
                  </a>
                ))}
              </Box>

              {loadingComments && (
                <Box sx={{ mt: 1 }}>
                  Loading comments...
                  <LinearProgress />
                </Box>
              )}
              {!loadingComments && (
                <div className="direct-chat-messages">
                  <div className="direct-chat-msg">
                    <div className="direct-chat-infos">
                      <span className="direct-chat-name">{selected.driver_name}</span>
                      <span className="direct-chat-timestamp">{moment.tz(selected.created_ts, moment.tz.guess()).format(DRIVER_APPEALS_DATETIME_FORMAT)}</span>
                    </div>
                    <img className="direct-chat-img" src={driverLogo} alt="Driver" />
                    <div className="direct-chat-text">{selected.reason}</div>
                  </div>
                  {replies?.map((reply) => (
                    <div className="direct-chat-msg right" key={`reply-${reply.id || reply.created_ts}`}>
                      <div className="direct-chat-infos">
                        <span className="direct-chat-name">Jitsu - {reply.user_name}</span>
                        <span className="direct-chat-timestamp">{moment.tz(reply.created_ts, moment.tz.guess()).format(DRIVER_APPEALS_DATETIME_FORMAT)}</span>
                      </div>
                      <img className="direct-chat-img" src={logo} alt="Jitsu" />
                      <div className="direct-chat-text">{reply.content}</div>
                    </div>
                  ))}
                </div>
              )}

              {!!replyDisabled && (
                <Box align="center" sx={{ my: 1 }}>
                  Reply is disabled
                </Box>
              )}
              {!replyDisabled && !loadingComments && (
                <Box className="box-footer">
                  <Box>
                    <TextareaAutosize
                      value={answer}
                      onChange={handleChange}
                      variant="outlined"
                      id="standard-start-adornment"
                      className={classes.answerInput}
                      placeholder={
                        'Please provide a final decision that is based upon your assessment of the driver’s performance relative to the TSA contract on file after considering any relevant arguments in the appeal'
                      }
                      minRows={5}
                      disabled={replyDisabled}
                      maxLength={2048}
                    />
                    <Box className={classes.error}>{error}</Box>
                  </Box>
                  <Box item xs={12}>
                    <Button onClick={() => sendMessage(STATUS_REJECTED)} variant="contained" color="secondary" className={classes.btnReject} disabled={replyDisabled}>
                      REJECT
                    </Button>
                    <Button onClick={() => sendMessage(STATUS_APPROVED)} variant="contained" color="success" className={classes.btnApprove} disabled={replyDisabled}>
                      APPROVE
                    </Button>
                  </Box>
                </Box>
              )}
            </Paper>
          </Grid>

          <Grid size={4}>
            <Paper className={classes.termination}>
              <h3 className={classes.title}>Termination Infos</h3>
              <Box className={classes.row}>
                <label>Type: </label>
                <span>{selected.termination_type}</span>
              </Box>
              <Box className={classes.row}>
                <label>Reason: </label>
                <span>{selected.termination_reason}</span>
              </Box>
              <Box className={classes.row}>
                <label>Category: </label>
                <span>{selected.termination_category}</span>
              </Box>
              <Box className={classes.row}>
                <label>Status: </label>
                <span>{selected.termination_status}</span>
              </Box>
              <Box className={classes.row}>
                <label>Created By: </label>
                <span>{selected.termination_created_by || '-'}</span>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </div>
      <Dialog open={open} onClose={handleClose} aria-labelledby="alert-dialog-title" aria-describedby="alert-dialog-description">
        <DialogTitle id="alert-dialog-title">{alertTitle}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">{alertContent}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="primary">
            Disagree
          </Button>
          <Button onClick={() => handleAccept(selectedStatus)} color="primary" autoFocus>
            Agree
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default Detail;
